import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { useOpenOnNew } from "@/hooks/useOpenOnNew";
import { Add, Account, Partners } from "@dreamhub/designsystem/icons";
import Text from "@/components/ui/text";
import CreatePersonModal from "@/components/layouts/CreatePersonModal";
import CreateContactModal from "@/components/layouts/CreateContactModal";
import PeopleTable from "@/components/layouts/People/PeopleTable";
import { usePeopleTable } from "@/services/hooks/entities/person/usePeopleTable";
import ToolbarActions from "@/components/controls/ToolbarActions/ToolbarActions";
import {
  EmptyView,
  ViewDataConfiguration,
  ViewType
} from "@/entities/views/types";
import { ColumnData } from "@/entities/dataTables/types";
import {
  getDefaultColumnsStateWithCustomFields,
  getPageNameFromTableId,
  excludeFieldsByKey,
  ICP_FIT_FIELD_KEYS
} from "@/entities/dataTables/helpers";
import { useReconciledColumnsState } from "@/entities/dataTables/hooks";
import { ICP_FIT_COLUMN_ID } from "@/entities/icpFit/helpers";
import {
  peopleColumnKeys,
  isPeopleViewParam,
  withPeopleViewParam
} from "@/entities/people/helpers";
import {
  PEOPLE_VIEW_PARAM,
  PeopleEntityView,
  PersonColumn
} from "@/entities/people/types";
import { UserData } from "@/entities/users/types";
import { IconButton } from "@/components/ui/icon-button";
import IconContainer from "@/components/ui/icon-container";
import ExportMenu from "@/components/ui/export-menu";
import { useFieldVisibility, useEntityFields } from "@/pages/settings/hooks";
import { EntityType } from "@/services/hooks/entities/useEntityType";
import EntityLogoIconContainer from "@/components/ui/entity-logo-icon-container";
import { EmptyFilters } from "@/services/api/types";
import { isEqual } from "lodash";
import DataTableToolbar from "@/components/controls/DataTableToolbar";
import PeopleBottomKPIBar from "@/components/controls/BottomKPIBar/PeopleBottomKPIBar";
import { useFeatureFlag } from "@/services/features/hook";
import { FeatureFlags } from "@/services/features/types";
import {
  ExportFormat,
  useEntityExport
} from "@/services/hooks/entities/useEntityExport";
import { ExportFn } from "@/services/hooks/entities/useExportButtonState";
import Tabs from "@/components/controls/Tabs";
import PartnerContacts from "@/pages/partners/contacts/PartnerContacts";
import CreatePartnerContactModal from "@/pages/partners/contacts/components/CreatePartnerContactModal";
import { useResetPartnerContactsInfinite } from "@/pages/partners/contacts/hooks";
import { useEnablePartnerships } from "@/pages/partners/hooks";

const TABLE_ID = "people-table";

interface Props {
  userData: UserData;
}

const People: React.FC<Props> = ({ userData }) => {
  const { t } = useTranslation();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [candidateData, clearCandidateData] = useOpenOnNew(setIsCreateModalOpen);
  const [viewReady, setViewReady] = useState(false);
  const [columnsState, setColumnsState] = useState<ColumnData[]>([]);
  const [selectedView, setSelectedView] = useState<ViewDataConfiguration>(
    EmptyView(ViewType.PEOPLE_TABLE)
  );

  const enablePartnerships = useEnablePartnerships();
  const { enabled: partnerContactsFlag } = useFeatureFlag(
    FeatureFlags.CRM_8228_PARTNER_CONTACTS_TABLE
  );
  const showPartnerPeople = partnerContactsFlag && enablePartnerships;

  const [searchParams, setSearchParams] = useSearchParams();
  const viewParam = searchParams.get(PEOPLE_VIEW_PARAM);
  const isPartnerPeopleView =
    viewParam === PeopleEntityView.PARTNER && showPartnerPeople;
  const setPeopleView = useCallback(
    (value: string) => {
      setSearchParams((prev) => withPeopleViewParam(prev, value), {
        replace: true
      });
    },
    [setSearchParams]
  );

  // Only writes once partnerships are known on, so a ?view=partner link isn't
  // clobbered while the flag and the account setting are still false.
  useEffect(() => {
    if (!showPartnerPeople || isPeopleViewParam(viewParam)) return;
    setPeopleView(PeopleEntityView.COMPANY);
  }, [showPartnerPeople, viewParam, setPeopleView]);
  const [isContactCreateModalOpen, setIsContactCreateModalOpen] =
    useState(false);
  const resetPartnerContacts = useResetPartnerContactsInfinite();

  const { data: entityFieldsResponse } = useEntityFields(EntityType.PERSON);
  const { enabled: isIcpFitEnabled } = useFeatureFlag(
    FeatureFlags.CRM_5676_ICP
  );
  const customFields = useMemo(() => {
    const fields = entityFieldsResponse?.fields || [];
    return isIcpFitEnabled
      ? fields
      : excludeFieldsByKey(fields, ICP_FIT_FIELD_KEYS);
  }, [entityFieldsResponse, isIcpFitEnabled]);
  const { filterVisibleColumnKeys } = useFieldVisibility(EntityType.PERSON);
  const activePeopleColumnKeys = useMemo(
    () => [
      ...filterVisibleColumnKeys(peopleColumnKeys),
      ...(isIcpFitEnabled ? [ICP_FIT_COLUMN_ID] : [])
    ],
    [filterVisibleColumnKeys, isIcpFitEnabled]
  );

  const hiddenByDefaultColumnIds = useMemo(
    () =>
      new Set<string>([
        PersonColumn.DESK_PHONE,
        PersonColumn.ADDRESS,
        PersonColumn.COUNTRY
      ]),
    []
  );

  const defaultColumnsState = useMemo(
    () =>
      getDefaultColumnsStateWithCustomFields(
        activePeopleColumnKeys,
        customFields,
        getPageNameFromTableId(TABLE_ID),
        t,
        undefined,
        undefined,
        hiddenByDefaultColumnIds
      ),
    [activePeopleColumnKeys, customFields, t, hiddenByDefaultColumnIds]
  );

  const flagGatedColumnIds = useMemo(
    () => (isIcpFitEnabled ? [ICP_FIT_COLUMN_ID] : []),
    [isIcpFitEnabled]
  );

  useReconciledColumnsState({
    columnsState,
    setColumnsState,
    defaultColumnsState,
    selectedView,
    flagGatedColumnIds,
    isReady: Boolean(entityFieldsResponse)
  });

  const handleColumnsChange = useCallback((updatedColumns?: ColumnData[]) => {
    if (updatedColumns) {
      setColumnsState(updatedColumns);
    }
  }, []);

  const {
    people,
    total,
    sorting,
    onSortingChange,
    isLoading,
    isError,
    refetch,
    isFetchingNextPage,
    hasNextPage,
    searchTerm,
    setSearchTerm,
    filters,
    setFilters,
    groupBy,
    setGroupBy,
    groupSummaries,
    peopleGroups,
    peopleFilterOptions,
    peopleSortByOptions,
    handleLoadMore,
    resetPage,
    markViewActivation
  } = usePeopleTable({ enabled: viewReady });

  const { exportTo: exportPeople, isExporting: isExportingPeople } =
    useEntityExport({
      createRoute: "people/export/create",
      statusRoute: "people/export/status",
      tableName: "people",
      filters,
      sorting,
      searchTerm,
      columnsState,
      totalRows: total,
      getFilterOptions: () => peopleFilterOptions
    });

  const contactsExportFnRef = useRef<ExportFn | null>(null);
  const [isExportingContacts, setIsExportingContacts] = useState(false);
  const handleContactsExportStateChange = useCallback(
    (exportTo: ExportFn, isExporting: boolean) => {
      contactsExportFnRef.current = exportTo;
      setIsExportingContacts(isExporting);
    },
    []
  );

  useEffect(() => {
    if (isPartnerPeopleView) return;
    contactsExportFnRef.current = null;
    setIsExportingContacts(false);
  }, [isPartnerPeopleView]);

  const exportMenu = isPartnerPeopleView
    ? {
        dataTest: "partner-contacts-export-button",
        onExport: (format: ExportFormat) =>
          contactsExportFnRef.current?.(format),
        isExporting: isExportingContacts
      }
    : {
        dataTest: "people-export-button",
        onExport: exportPeople,
        isExporting: isExportingPeople
      };

  const handleViewActivated = useCallback(() => {
    markViewActivation();
    setViewReady(true);
  }, [markViewActivation]);

  const handleOpenCreateModal = () => {
    setIsCreateModalOpen(true);
  };

  // One object for both create modals: a branch that misses the candidate pair
  // silently drops the deep-linked email, so the two arms must not carry it separately.
  const createModalProps = {
    isOpen: isCreateModalOpen,
    setIsOpen: setIsCreateModalOpen,
    resetPage,
    allowDreamer: true,
    defaultEmail: candidateData?.email,
    onCandidateDataConsumed: clearCandidateData
  };

  return (
    <div data-test="people-page" className="h-full">
      <div
        data-test="people-content"
        className="flex flex-col max-h-[calc(var(--app-content-h)-var(--table-bottom-space))] h-full"
      >
        <div
          data-test="people-header"
          className="flex justify-between p-4 pb-2 h-14"
        >
          <div
            data-test="people-title"
            className="flex items-center gap-[10px]"
          >
            <EntityLogoIconContainer entityType={EntityType.PERSON} />
            <Text as="div" variant="lgSubtitleH1" className="p-1">
              {t("people.title")}
            </Text>
          </div>
          <div data-test="people-actions" className="flex items-center gap-2">
            {showPartnerPeople && (
              <Tabs
                options={{
                  [PeopleEntityView.COMPANY]: (
                    <span className="flex items-center gap-1.5">
                      <IconContainer size="sm" color="inherit">
                        <Account />
                      </IconContainer>
                      {t("partnercontacts.toggle.company")}
                    </span>
                  ),
                  [PeopleEntityView.PARTNER]: (
                    <span className="flex items-center gap-1.5">
                      <IconContainer size="sm" color="inherit">
                        <Partners />
                      </IconContainer>
                      {t("partnercontacts.toggle.partner")}
                    </span>
                  )
                }}
                value={
                  isPartnerPeopleView
                    ? PeopleEntityView.PARTNER
                    : PeopleEntityView.COMPANY
                }
                valueSelected={(value) => setPeopleView(value)}
                size="sm"
                containerClassName="w-fit"
                dataTest="people-entity-toggle"
              />
            )}
            <ExportMenu
              label={t("export")}
              data-test={exportMenu.dataTest}
              onExport={exportMenu.onExport}
              isExporting={exportMenu.isExporting}
            />
            <IconButton
              data-test={
                isPartnerPeopleView
                  ? "partner-contact-create-button"
                  : "people-create-button"
              }
              onClick={
                isPartnerPeopleView
                  ? () => setIsContactCreateModalOpen(true)
                  : handleOpenCreateModal
              }
              size="lg"
            >
              <Add />
            </IconButton>
          </div>
        </div>
        {isPartnerPeopleView ? (
          <PartnerContacts
            userData={userData}
            onExportStateChange={handleContactsExportStateChange}
          />
        ) : (
          <>
            <DataTableToolbar
              data-test="people-toolbar"
              viewType={ViewType.PEOPLE_TABLE}
          selectedView={selectedView}
          setSelectedView={setSelectedView}
          filterOptions={peopleFilterOptions}
          filters={filters}
          setFilters={setFilters}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          groupBy={groupBy}
          setGroupBy={setGroupBy}
          sorting={sorting}
          defaultColumnsState={getDefaultColumnsStateWithCustomFields(
            activePeopleColumnKeys,
            customFields,
            getPageNameFromTableId(TABLE_ID),
            t,
            undefined,
            undefined,
            hiddenByDefaultColumnIds
          )}
          columnsState={columnsState}
          setColumnsState={setColumnsState}
          sortByOptions={peopleSortByOptions}
          defaultSortByValue="fullName"
          onSortingChange={onSortingChange}
          tableId={TABLE_ID}
          onColumnsChange={handleColumnsChange}
          customFields={customFields}
          onViewActivated={handleViewActivated}
        />
        <ToolbarActions
          data-test="people-toolbar-actions"
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          filters={filters}
          setFilters={setFilters}
          filterOptions={peopleFilterOptions}
          groupBy={groupBy}
          setGroupBy={setGroupBy}
          sorting={sorting}
          onSortingChange={onSortingChange}
          sortByOptions={peopleSortByOptions}
          defaultSortByValue="fullName"
          columnsState={columnsState}
          setColumnsState={setColumnsState}
          defaultColumnsState={getDefaultColumnsStateWithCustomFields(
            activePeopleColumnKeys,
            customFields,
            getPageNameFromTableId(TABLE_ID),
            t,
            undefined,
            undefined,
            hiddenByDefaultColumnIds
          )}
          selectedView={selectedView}
          setSelectedView={setSelectedView}
          viewType={ViewType.PEOPLE_TABLE}
          onColumnsChange={handleColumnsChange}
          tableId={TABLE_ID}
          customFields={customFields}
          className="p-2 pr-4"
        />
        <PeopleTable
          data-test="people-table"
          people={people}
          sorting={sorting}
          onSortingChange={onSortingChange}
          isLoading={isLoading || !viewReady}
          isFetchingNextPage={isFetchingNextPage}
          hasNextPage={hasNextPage}
          onLoadMore={handleLoadMore}
          tableId={TABLE_ID}
          selectedView={selectedView}
          columnsState={columnsState}
          onColumnsChange={handleColumnsChange}
          userData={userData}
          isCompanyColumnSecond
          className="h-full"
          groupBy={groupBy}
          groups={peopleGroups}
          groupSummaries={groupSummaries}
          filterOptions={peopleFilterOptions}
          filters={filters}
          setFilters={setFilters}
          hasActiveFilters={!isEqual(filters, EmptyFilters)}
          onClearFilters={() => setFilters(EmptyFilters)}
          onClearSearch={() => setSearchTerm("")}
          isError={isError}
          onRetry={refetch}
        />
            <PeopleBottomKPIBar totalPeople={total} />
          </>
        )}
      </div>
      {showPartnerPeople ? (
        <CreateContactModal {...createModalProps} />
      ) : (
        <CreatePersonModal
          data-test="people-create-modal"
          {...createModalProps}
        />
      )}
      {showPartnerPeople && (
        <CreatePartnerContactModal
          isOpen={isContactCreateModalOpen}
          setIsOpen={setIsContactCreateModalOpen}
          resetPage={resetPartnerContacts}
        />
      )}
    </div>
  );
};

export default People;
